// src/plugins/sales-forecast/components/DomPredictionPanel.tsx
import React, { useEffect, useMemo, useState } from "react";
import { Params } from "./ControlPanel";
import { useSystemTheme } from "../hooks/useSystemTheme";
import "../../sales-forecast/styles/theme.css";

import { Card, CardHeader, CardContent } from "../../../components/ui/card";

const api_base = process.env.REACT_APP_API_BASE;

interface DomPredictionPanelProps {
  params: Params;
  trigger: boolean;
  setTrigger: React.Dispatch<React.SetStateAction<boolean>>;
  setDomPredictions: React.Dispatch<
    React.SetStateAction<{ [key: string]: number | null } | null>
  >;
}

type PredictionResult = { [key: string]: number | null };

// ---------- Helper: Convert days → months ----------
function daysToMonths(days: number | null | undefined): number | null {
  if (days == null || !Number.isFinite(days)) return null;
  const months = days / 30;
  return Math.round(months * 10) / 10;
}



// ============================================================================
// Component
// ============================================================================
export const DomPredictionPanel: React.FC<DomPredictionPanelProps> = ({
  params,
  trigger,
  setTrigger,
  setDomPredictions,
}) => {
  const theme = useSystemTheme();

  const [footingPredictions, setFootingPredictions] =
    useState<PredictionResult | null>(null);
  const [countertopsPredictions, setCountertopsPredictions] =
    useState<PredictionResult | null>(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ========================================================================
  // Build request body
  // ========================================================================
  const requestBody = useMemo(() => {
    const livingAreaMean =
      (Number(params.squareFootMin ?? 0) + Number(params.squareFootMax ?? 0)) / 2;

    return {
      threeMonthPriceChange: Number(params.threeMonthPriceChange ?? 0),
      sixMonthPriceChange: Number(params.sixMonthPriceChange ?? 0),
      nineMonthPriceChange: Number(params.nineMonthPriceChange ?? 0),
      twelveMonthPriceChange: Number(params.twelveMonthPriceChange ?? 0),
      twoYearPriceChange: Number(params.twoYearPriceChange ?? 0),
      bedrooms: Number(params.bedrooms ?? 0),
      stories: Number(params.stories ?? 0),
      yearBuilt: Number(params.yearBuilt ?? 0),
      living_area_sqft_standardized: Number.isFinite(livingAreaMean) ? livingAreaMean : 0,
      activeComps: Number(params.activeComps ?? 0),
      interestRate: Number(params.interestRate ?? 0),
      interestRateThreeMonthChange: Number(params.interestRateThreeMonthChange ?? 0),
      interestRateSixMonthChange: Number(params.interestRateSixMonthChange ?? 0),
      interestRateNineMonthChange: Number(params.interestRateNineMonthChange ?? 0),
      interestRateTwelveMonthChange: Number(params.interestRateTwelveMonthChange ?? 0),
      interestRateTwoYearChange: Number(params.interestRateTwoYearChange ?? 0),
      garageSpaces: Number(params.garageSpaces ?? 0),
      amenities: Array.isArray(params.amenities) ? params.amenities : [],
      listing_day: Number(params.listingDay ?? 1),
      listing_month: String(params.listingMonth ?? ""),
    };
  }, [params]);

  // ============================================================================
  // Fetch predictions
  // ============================================================================
  useEffect(() => {
    if (!trigger) return;

    let isCancelled = false;

    const fetchPredictions = async () => {
      setLoading(true);
      setError(null);
      setFootingPredictions(null);
      setCountertopsPredictions(null);

      const fetchPhase = async (phase: string): Promise<PredictionResult> => {
        const url = `${api_base}/predict?listing_phase=${encodeURIComponent(
          phase
        )}&cloneType=${encodeURIComponent(params.cloneType || "")}&neighborhoodTitle=${encodeURIComponent(
          (params.neighborhoodTitle || "").toLowerCase()
        )}`;

        const res = await fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestBody),
        });

        if (!res.ok) {
          let details = "";
          try {
            const errJson = await res.json();
            details = typeof errJson?.error === "string" ? ` — ${errJson.error}` : "";
          } catch {
            // ignore
          }
          throw new Error(`Error ${res.status}: ${res.statusText}${details}`);
        }

        const json = (await res.json()) as PredictionResult;

        const converted: PredictionResult = {};
        for (const key in json) converted[key] = daysToMonths(json[key]);
        return converted;
      };

      try {
        const [footing, countertops] = await Promise.all([
          fetchPhase("Footing"),
          fetchPhase("Countertops"),
        ]);

        if (!isCancelled) {
          setFootingPredictions(footing);
          setCountertopsPredictions(countertops);
          setDomPredictions({ footing, countertops } as any);
        }
      } catch (err: any) {
        if (!isCancelled) {
          setError(err?.message || "Unknown error");
          setDomPredictions(null);
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
          setTrigger(false);
        }
      }
    };

    fetchPredictions();
    return () => {
      isCancelled = true;
    };
  }, [trigger, requestBody, setTrigger, params.cloneType, params.neighborhoodTitle, setDomPredictions]);

  // ============================================================================
  // UI Rendering
  // ============================================================================
  const orderKeys = ["dom_85pct", "dom_90pct", "dom_95pct", "dom_99pct"] as const;

  const borderColor = "var(--border-color)";
  const textColor = "var(--text-color)";
  const mutedText = "var(--muted-text-color)";

  // Darken the inner prediction tiles
  const predTileBg = "var(--subcard-bg)";

  const PredictionCard: React.FC<{ label: string; value: number | null }> = ({
    label,
    value,
  }) => {
    return (
      <div
        style={{
          backgroundColor: predTileBg,
          border: `1px solid ${borderColor}`,
          borderRadius: "10px",
          padding: "0.85rem 1rem",
          textAlign: "center",
          boxShadow: "0 1px 4px rgba(0,0,0,0.25)",
          minHeight: "4.75rem",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          gap: "0.35rem",
          marginBottom: "0.4rem",
        }}
      >
        <div style={{ fontSize: "0.9rem", fontWeight: 600, color: textColor }}>
          {label}
        </div>
        <div style={{ fontFamily: "monospace", fontSize: "0.9rem", color: textColor }}>
          {value == null ? "N/A" : `${value.toFixed(1)} months`}
        </div>
      </div>
    );
  };

  // ============================================================================
  // Final Render (containerized like Summary Statistics)
  // ============================================================================
  return (
    <div className={`dom-prediction-panel theme-${theme}`}>
      <Card
        style={{
          backgroundColor: "var(--card-bg)",
          color: "var(--text-color)",
          border: "1px solid var(--border-color)",
        }}
        className="rounded-xl shadow-md"
      >
        <CardHeader />
        <CardContent className="space-y-4" style={{ color: "var(--text-color)" }}>
          <h3 className="text-lg font-bold text-center">Months on Market Predictions</h3>

          {loading && (
            <p className="italic text-center" style={{ color: mutedText, fontSize: "0.95rem" }}>
              Loading predictions...
            </p>
          )}

          {!loading && error && (
            <p className="text-center font-medium" style={{ color: "var(--error-text)" }}>
              Error: {error}
            </p>
          )}

          {!loading && footingPredictions && countertopsPredictions && (
            <div className="w-full flex flex-col items-center">
              <div className="grid grid-cols-2 gap-x-6 w-full max-w-md">
                <h4 className="text-center font-semibold mb-1" style={{ color: textColor }}>
                  Footing
                </h4>
                <h4 className="text-center font-semibold mb-1" style={{ color: textColor }}>
                  Countertops
                </h4>

                {orderKeys.map((key) => (
                  <React.Fragment key={key}>
                    <PredictionCard
                      label={key.replace("dom_", "").replace("pct", "% Likelihood")}
                      value={footingPredictions[key]}
                    />
                    <PredictionCard
                      label={key.replace("dom_", "").replace("pct", "% Likelihood")}
                      value={countertopsPredictions[key]}
                    />
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};